import React from "react";

import { closestCenter, DndContext, DragOverlay } from "@dnd-kit/core";
import {
  rectSortingStrategy,
  SortableContext,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVertical } from "lucide-react";

import {
  renumberSortOrder,
  SORTABLE_DROP_ANIMATION,
  SORTABLE_TRANSITION,
  useSortableListState,
  useSortableSensors,
} from "./sortableDnd";
import TruncatedTooltip from "../../Components/TruncatedTooltip";
import { VideoTopic } from "../../Store/Reducers/VideoSlice";

interface SortableTopicGridProps {
  topics: VideoTopic[];
  onTopicsChange: (topics: VideoTopic[]) => void;
  highlightId?: string | null;
  onMoved?: (id: string) => void;
}

const getTopicId = (topic: VideoTopic) => topic.id;

const TopicOrderCard: React.FC<{
  topic: VideoTopic;
  displayOrder: number;
}> = ({ topic, displayOrder }) => (
  <article
    className={`topic-card topic-card--sortable${
      topic.is_active === false ? " topic-card--inactive" : ""
    }`}
  >
    {/* Leave top-right free for the floating Drag handle. */}
    <div className="topic-card__top">
      <span className="topic-row__position">{displayOrder}</span>
    </div>

    <div className="topic-card__body">
      <TruncatedTooltip tooltip={topic.label} className="topic-card__title">
        {topic.label}
      </TruncatedTooltip>
      <div className="topic-card__meta">
        <p className="topic-card__count">
          {topic.video_count} {topic.video_count === 1 ? "video" : "videos"}
        </p>
        <span
          className={`topic-row__status-pill${
            topic.is_active === false ? " topic-row__status-pill--off" : ""
          }`}
        >
          {topic.is_active === false ? "Inactive" : "Active"}
        </span>
      </div>
    </div>
  </article>
);

const SortableTopicItem: React.FC<{
  topic: VideoTopic;
  displayOrder: number;
  highlighted: boolean;
}> = ({ topic, displayOrder, highlighted }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: topic.id,
    transition: SORTABLE_TRANSITION,
  });

  const setRefs = (node: HTMLDivElement | null) => {
    setNodeRef(node);
    setActivatorNodeRef(node);
  };

  return (
    <div
      ref={setRefs}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={`sortable-video${isDragging ? " is-placeholder is-drag-active" : ""}${
        highlighted ? " is-reorder-highlight" : ""
      }`}
      {...attributes}
      {...listeners}
      aria-label={`${topic.label} — drag to reorder`}
    >
      <span className="sortable-video__handle" aria-hidden="true">
        <GripVertical size={14} />
        Drag
      </span>
      <TopicOrderCard topic={topic} displayOrder={displayOrder} />
    </div>
  );
};

/** Drag-and-drop grid for Topics Edit Order (card layout). */
const SortableTopicGrid: React.FC<SortableTopicGridProps> = ({
  topics,
  onTopicsChange,
  highlightId = null,
  onMoved,
}) => {
  const sensors = useSortableSensors();
  const {
    activeId,
    ids,
    reset,
    handleDragStart,
    handleDragOver,
    handleDragEnd,
    previewOrders: buildPreviewOrders,
  } = useSortableListState({
    items: topics,
    getId: getTopicId,
    onItemsChange: onTopicsChange,
    onMoved,
    transformMoved: renumberSortOrder,
  });

  const previewOrders = buildPreviewOrders(
    topic => topic.sort_order,
    renumberSortOrder
  );

  const orderOf = (topic: VideoTopic) =>
    previewOrders.get(topic.id) ?? topic.sort_order ?? 0;

  const activeTopic = activeId
    ? topics.find(topic => topic.id === activeId)
    : undefined;

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragOver={handleDragOver}
      onDragEnd={handleDragEnd}
      onDragCancel={reset}
    >
      <SortableContext items={ids} strategy={rectSortingStrategy}>
        <div
          className={`topic-card-grid sortable-video-grid${
            activeId ? " is-sorting" : ""
          }`}
        >
          {topics.map(topic => (
            <SortableTopicItem
              key={topic.id}
              topic={topic}
              displayOrder={orderOf(topic)}
              highlighted={highlightId === topic.id}
            />
          ))}
        </div>
      </SortableContext>
      <DragOverlay dropAnimation={SORTABLE_DROP_ANIMATION}>
        {activeTopic ? (
          <div className="sortable-video sortable-video--overlay">
            <span className="sortable-video__handle" aria-hidden="true">
              <GripVertical size={14} />
              Drag
            </span>
            <TopicOrderCard
              topic={activeTopic}
              displayOrder={orderOf(activeTopic)}
            />
          </div>
        ) : null}
      </DragOverlay>
    </DndContext>
  );
};

export default SortableTopicGrid;
